import React, { useCallback } from 'react';
import cn from 'classnames';
import config from '@dataroom/config';
import FinalForm, { Field } from '@framework/ui/Organisms/FinalForm';
import Textarea from '@dealroadshow/uikit-textarea';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import Modal from '@dealroadshow/uikit-modal';
import Input from '@dealroadshow/uikit-input';
import validateForm, { validateVerisendForm } from './validator';
import { Select } from '@framework/ui/Organisms/FinalForm/Fields/Select';
import Button, { ButtonVariantType } from '@dealroadshow/uikit-button';
import { useDataroomTenantContext } from '@dataroom/tenant/DataroomTenantContext';
import { useAccessRequestContext } from '../../../../contexts/AccessRequestContext';
import styles from './accessRequestModal.module.scss';
import { TFinalFormRenderProp } from '@framework/ui/Organisms/FinalForm/interfaces';

type TAccessRequestFormValues = {
  role: string,
  message: string,
  salesRepresentative?: string,
};

export const listRoles = [
  {
    label: 'Broker-Dealer',
    value: 'broker_dealer',
  },
  {
    label: 'Data Collection Agent',
    value: 'data_collection_agent',
  },
  {
    label: 'Investor (Existing Holder)',
    value: 'investor_existing_holder',
  },
  {
    label: 'Investor (Prospective Holder)',
    value: 'investor_prospective_holder',
  },
  {
    label: 'Middle-Office / Operations',
    value: 'middle_office_operations',
  },
  {
    label: 'Rating Agency',
    value: 'rating_agency',
  },
  {
    label: 'Research Provider',
    value: 'research_provider',
  },
  {
    label: 'Servicer',
    value: 'servicer',
  },
  {
    label: 'Trustee',
    value: 'trustee',
  },
  {
    label: 'Other (please specify below)',
    value: 'other',
  },
];

interface IProps {
  getCollection: () => void,
}

const AccessRequestModal = ({ getCollection }: IProps) => {
  const { tenant } = useDataroomTenantContext();
  const { tenantLabel, label } = config[tenant].options;
  const {
    isFetching,
    dataroom,
    isAccessRequestModalVisible,
    setIsAccessRequestModalVisible,
    sendAccessRequest,
  } = useAccessRequestContext();

  const isVerisendOrResearchRoomTenant = (
    tenant === config.verisend.code ||
    tenant === config.researchroom.code
  );

  const isStagesEnabled = config[tenant].options;

  const handleFormSubmit = (formData: TAccessRequestFormValues) => sendAccessRequest(formData)
    .then(getCollection);

  const handleCloseAccessRequestModal = () => setIsAccessRequestModalVisible(false);

  const renderForm = useCallback<TFinalFormRenderProp<TAccessRequestFormValues>>(({
    submitFailed,
    invalid,
  }) => (
    <>
      <Field
        name="role"
        label="Role"
        placeholder="Select your role"
        component={ Select }
        options={ listRoles }
        isSimpleValue
        asterisk
        dataTest="roleAccessRequestModalReduxFormSelect"
      />
      { isVerisendOrResearchRoomTenant && (
        <Field
          name="salesRepresentative"
          label="Sales Representative"
          placeholder="Select your sales representative"
          component={ Input }
          dataTest="salesRepresentativeInput"
        />
      ) }
      <Field
        name="message"
        label="Message (Optional)"
        rows={ 8 }
        component={ Textarea }
        placeholder={ isStagesEnabled ? 'Additional notes and comments' : 'Optional message for Set Administrator' }
        maxLength={ 200 }
        isEnabledCountdownMessage
        dataTest="messageForSetAdministratorTextarea"
      />
      <Button
        variant={ ButtonVariantType.action }
        type="submit"
        title="Submit"
        dataTest="Submit"
        disabled={ submitFailed && invalid }
      />
      <Button
        onClick={ handleCloseAccessRequestModal }
        variant={ ButtonVariantType.outline }
        className={ styles.closeBtn }
        title="Cancel"
        dataTest="Cancel"
      />
      <Spinner
        isVisible={ isFetching }
        overlay
      />
    </>
  ), [isFetching]);

  if (!isAccessRequestModalVisible) {
    return null;
  }

  return (
    <Modal
      title={ `${ isStagesEnabled ? label : tenantLabel } Access Request` }
      onCloseClicked={ handleCloseAccessRequestModal }
      isVisible
      className={ styles.modal }
      onOverlayClicked={ handleCloseAccessRequestModal }
      dataTest="accessRequestForm"
    >
      <>
        <p className={ styles.labelBlock }>
          To complete your access request for <span>{ dataroom!.name }</span>,
          please provide the below information.
        </p>
        <Spinner
          isVisible={ isFetching && !isAccessRequestModalVisible }
          className={ styles.isFormLoadingSpinner }
          overlay
        />
        <FinalForm<TAccessRequestFormValues>
          dataTest="accessRequestForm"
          onSubmit={ handleFormSubmit }
          render={ renderForm }
          className={ cn(styles.form) }
          validate={ isVerisendOrResearchRoomTenant ? validateVerisendForm : validateForm }
        />
      </>
    </Modal>
  );
};

export default AccessRequestModal;
